import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Typography,
    CircularProgress,
    InputAdornment,
    IconButton,
    Card,
    CardContent,
    Stack,
    Divider
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Search as SearchIcon, Close as CloseIcon } from '@mui/icons-material';
import listaPrecioService, { ListaPrecio } from '../services/listaPrecioService';

interface ListaPrecioSearchProps {
    open: boolean;
    onClose: () => void;
    onSelect: (listaPrecio: ListaPrecio) => void;
}

const ListaPrecioSearch: React.FC<ListaPrecioSearchProps> = ({ open, onClose, onSelect }) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [searchTerm, setSearchTerm] = useState('');
    const [listasPrecios, setListasPrecios] = useState<ListaPrecio[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [verTodos, setVerTodos] = useState<boolean>(false);

    const handleSearch = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            if (verTodos) setVerTodos(false);
            const trimmed = searchTerm.trim();
            const compact = trimmed.replace(/\s+/g, '');
            
            // Si es código de lista, buscar directamente
            if (compact !== '') {
                try {
                    // Intentar obtener por código primero
                    const lista = await listaPrecioService.getListaPrecio(compact);
                    setListasPrecios(lista ? [lista] : []);
                    if (!lista) {
                        // Si no se encuentra por código, buscar por descripción
                        const response = await listaPrecioService.getListaPrecios({
                            searchTerm: trimmed,
                            pageNumber: 1,
                            pageSize: 50
                        });
                        setListasPrecios(response.items || []);
                        if (!response.items || response.items.length === 0) {
                            setError('No se encontraron listas de precios.');
                        }
                    }
                } catch (err: any) {
                    // Si falla la búsqueda por código, buscar por descripción
                    try {
                        const response = await listaPrecioService.getListaPrecios({
                            searchTerm: trimmed,
                            pageNumber: 1,
                            pageSize: 50
                        });
                        setListasPrecios(response.items || []);
                        if (!response.items || response.items.length === 0) {
                            setError('No se encontraron listas de precios.');
                        }
                    } catch (searchErr: any) {
                        setListasPrecios([]);
                        setError('No se encontraron listas de precios.');
                    }
                } finally {
                    setLoading(false);
                }
                return;
            }

            // Si no hay término de búsqueda, obtener todas
            const response = await listaPrecioService.getListaPrecios({
                searchTerm: undefined,
                pageNumber: 1,
                pageSize: 50
            });
            setListasPrecios(response.items || []);
            if (!response.items || response.items.length === 0) {
                setError('No se encontraron listas de precios.');
            }
        } catch (err: any) {
            setError(`Error al buscar listas de precios: ${err.message || 'Error desconocido'}`);
            setListasPrecios([]);
        } finally {
            setLoading(false);
        }
    }, [searchTerm, verTodos]);

    const handleVerTodos = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            setVerTodos(true);
            const pageSize = 1000;
            
            // Obtener todas las listas sin parámetros de búsqueda
            const response = await listaPrecioService.getListaPrecios();
            
            let all: ListaPrecio[] = response.items || [];
            setListasPrecios(all);
            
            // Si hay paginación, obtener todas las páginas
            const totalPages = response.totalPages || 1;
            if (totalPages > 1) {
                const pages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
                const promises = pages.map((page) =>
                    listaPrecioService
                        .getListaPrecios({ searchTerm: undefined, pageNumber: page, pageSize })
                        .then((resp) => {
                            if (resp && Array.isArray(resp.items)) {
                                const items = resp.items as ListaPrecio[];
                                setListasPrecios((prev) => prev.concat(items));
                                all = all.concat(items);
                            }
                        })
                        .catch(() => {})
                );
                await Promise.all(promises);
            }
            if (all.length === 0) {
                setError('No se encontraron listas de precios.');
            }
        } catch (err: any) {
            setError(`Error al cargar listas de precios: ${err.message || 'Error desconocido'}`);
            setListasPrecios([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleSelect = (listaPrecio: ListaPrecio) => {
        onSelect(listaPrecio);
        onClose();
    };

    const handleClose = () => {
        setSearchTerm('');
        setListasPrecios([]);
        setError(null);
        onClose();
    };

    // Limpia al cerrar
    useEffect(() => {
        if (!open) {
            setSearchTerm('');
            setListasPrecios([]);
            setError(null);
            setVerTodos(false);
            return;
        }
    }, [open]);

    // Búsqueda automática cuando cambia el término de búsqueda
    useEffect(() => {
        if (!open) return;
        
        const handle = setTimeout(() => {
            if (searchTerm.trim()) {
                handleSearch();
            } else {
                // Si no hay término de búsqueda, cargar todas las listas
                handleVerTodos();
            }
        }, 400);
        
        return () => clearTimeout(handle);
    }, [open, searchTerm, handleSearch, handleVerTodos]);

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            fullScreen={isSmallScreen}
            sx={{
                '& .MuiDialog-paper': {
                    margin: { xs: 0, sm: 2 },
                    maxHeight: { xs: '100%', sm: '90vh' },
                    height: { xs: '100%', sm: 'auto' }
                }
            }}
        >
            <DialogTitle sx={{ pb: { xs: 1, sm: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                        Buscar Lista de Precios
                    </Typography>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            
            <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 1, sm: 2 } }}>
                <Box sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Código de lista o descripción"
                        inputProps={{ 'aria-label': 'Buscar lista de precios' }}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleSearch();
                            }
                        }}
                        size={isSmallScreen ? 'small' : 'medium'}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={handleSearch} disabled={loading} edge="end">
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                        sx={{ mb: 1.5 }}
                    />
                    <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                        <Button 
                            variant="outlined" 
                            onClick={handleSearch}
                            disabled={loading}
                            size={isSmallScreen ? 'medium' : 'small'}
                            fullWidth={isSmallScreen}
                            startIcon={<SearchIcon />}
                        >
                            BUSCAR
                        </Button>
                        <Button 
                            variant="text" 
                            onClick={handleVerTodos}
                            disabled={loading}
                            size={isSmallScreen ? 'medium' : 'small'}
                            fullWidth={isSmallScreen}
                        >
                            VER TODOS
                        </Button>
                    </Box>
                </Box>

                {error && (
                    <Typography color="error" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        {error}
                    </Typography>
                )}

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                )}

                {listasPrecios.length > 0 && (
                    <>
                        {isSmallScreen ? (
                            // Vista de tarjetas para móviles
                            <Box sx={{ maxHeight: '60vh', overflowY: 'auto', pr: 1 }}>
                                <Stack spacing={2}>
                                    {listasPrecios.map((lista) => (
                                        <Card 
                                            key={lista.codigoLista.trim()} 
                                            sx={{ 
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    boxShadow: 3
                                                }
                                            }}
                                            onClick={() => handleSelect(lista)}
                                        >
                                            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                    <Typography variant="subtitle2" color="primary" fontWeight="bold">
                                                        Código: {lista.codigoLista.trim()}
                                                    </Typography>
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleSelect(lista);
                                                        }}
                                                        sx={{ minWidth: 'auto', px: 2 }}
                                                    >
                                                        Seleccionar
                                                    </Button>
                                                </Box>
                                                <Divider sx={{ my: 1.5 }} />
                                                <Stack spacing={1}>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" display="block">
                                                            Descripción
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {lista.descripcion?.trim() || '-'}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Stack>
                            </Box>
                        ) : (
                            // Vista de tabla para pantallas grandes
                            <TableContainer 
                                component={Paper} 
                                sx={{ 
                                    maxHeight: 400,
                                    '& .MuiTableCell-root': {
                                        py: 1.5,
                                        fontSize: '0.875rem'
                                    }
                                }}
                            >
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>Código</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', minWidth: 300 }}>Descripción</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', width: 120 }} align="center">Acción</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {listasPrecios.map((lista) => (
                                            <TableRow 
                                                key={lista.codigoLista.trim()} 
                                                hover
                                                sx={{ cursor: 'pointer' }}
                                                onClick={() => handleSelect(lista)}
                                            >
                                                <TableCell>{lista.codigoLista.trim()}</TableCell>
                                                <TableCell>{lista.descripcion?.trim() || '-'}</TableCell>
                                                <TableCell align="center">
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleSelect(lista);
                                                        }}
                                                    >
                                                        Seleccionar
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </>
                )}

                {!loading && listasPrecios.length === 0 && searchTerm && (
                    <Typography color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
                        No se encontraron listas de precios
                    </Typography>
                )}
            </DialogContent>

            <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 }, flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
                <Button 
                    onClick={handleClose} 
                    fullWidth={isSmallScreen}
                    size={isSmallScreen ? 'medium' : 'small'}
                    variant="outlined"
                >
                    CANCELAR
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ListaPrecioSearch;

